import { SITE } from "@/components/site/Seo";

const localityRegion = (location) => {
  const [locality, region] = location.split(",").map((s) => s.trim());
  return { locality: locality || "Wilton Manors", region: region || "FL" };
};

// "November 2025" -> "2025-11-01" (schema datePublished). Returns undefined if unparseable.
const MONTHS = {
  january: "01", february: "02", march: "03", april: "04", may: "05", june: "06",
  july: "07", august: "08", september: "09", october: "10", november: "11", december: "12",
};
const isoFromMonthYear = (str) => {
  if (!str) return undefined;
  const [m, y] = str.trim().toLowerCase().split(/\s+/);
  if (MONTHS[m] && /^\d{4}$/.test(y)) return `${y}-${MONTHS[m]}-01`;
  return undefined;
};

// Rich per-property structured data for AI search + Google rich results.
export function propertyJsonLd(p, reviews = []) {
  const url = `${SITE}/properties/${p.slug}`;
  const { locality, region } = localityRegion(p.location);
  const accommodation = {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    "@id": `${url}#listing`,
    name: p.name,
    description: p.description,
    url,
    image: p.gallery && p.gallery.length ? p.gallery : [p.image],
    numberOfBedrooms: p.bedrooms,
    numberOfBathroomsTotal: p.bathrooms,
    occupancy: { "@type": "QuantitativeValue", maxValue: p.guests, unitText: "guests" },
    petsAllowed: (p.amenities || []).some((a) => /pet/i.test(a)),
    address: {
      "@type": "PostalAddress",
      addressLocality: locality,
      addressRegion: region,
      addressCountry: "US",
    },
    geo: p.coords
      ? { "@type": "GeoCoordinates", latitude: p.coords[0], longitude: p.coords[1] }
      : undefined,
    amenityFeature: (p.amenities || []).map((a) => ({
      "@type": "LocationFeatureSpecification",
      name: a,
      value: true,
    })),
    brand: { "@type": "Organization", name: "Double Rainbow Suites", "@id": `${SITE}/#business` },
    potentialAction: {
      "@type": "ReserveAction",
      target: { "@type": "EntryPoint", urlTemplate: p.bookingUrl },
    },
  };
  if (p.rating && p.reviewCount) {
    accommodation.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: p.rating.toFixed(1),
      reviewCount: p.reviewCount,
      bestRating: "5",
    };
  }
  if (reviews && reviews.length) {
    accommodation.review = reviews.map((r) => ({
      "@type": "Review",
      itemReviewed: { "@type": "VacationRental", "@id": `${url}#listing`, name: p.name },
      author: { "@type": "Person", name: r.name },
      datePublished: isoFromMonthYear(r.date),
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: "5", worstRating: "1" },
      reviewBody: r.text,
    }));
  }
  return accommodation;
}

export function breadcrumbJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE}${it.path}`,
    })),
  };
}

export function faqJsonLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, "").trim() },
    })),
  };
}
